/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import MUICheckbox from '@mui/material/Checkbox'; // Keep for expand/collapse checkbox
import cx from 'classnames';
import * as _ from 'lodash';
import React from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { useQuery as useQueryParams } from '../../../../contexts/RouterContext';
import { Icon, icons, LoaderButton } from '../../../generic';
import Checkbox from '../../../generic/Forms/Checkbox';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import PackageLabelsList from '../../../shared/packageLabels/PackageLabelsList';
import useSnippetsForPackage from '../hooks/useSnippetsForPackage';
import type { SnippetPackage, SnippetsQueryParams } from '../../../../../shared/snippets';
import type { SnippetSelectionState } from '../hooks/useSnippetSelectionState';

import IssueCounts from './IssueCounts';
import LoaderRow from './LoaderRow';
import SnippetRow from './SnippetRow';

import styles from './index.module.less';

export type Props = {
  snippetPackage: SnippetPackage;
  selectionState: SnippetSelectionState;
  shouldShowBorderTop: boolean;
  shouldShowBorderBottom: boolean;
};

/**
 * Displays snippet package row for the snippets table.
 */
const SnippetPackageRow: VoidFunctionComponent<Props> = ({
  snippetPackage,
  selectionState,
  shouldShowBorderTop,
  shouldShowBorderBottom,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [queryParams, setQuery] = useQueryParams<SnippetsQueryParams>();
  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isInitialLoading,
  } = useSnippetsForPackage(snippetPackage.id, isExpanded);

  return (
    <>
      <div
        key={snippetPackage.id}
        className={cx(styles.tableBodyRow, styles.tableBodyRowPackages, {
          [styles.tableBodyRowRejected]: snippetPackage.isFullyRejected,
          [styles.tableBodyRowBorderTop]: shouldShowBorderTop,
          [styles.tableBodyRowBorderBottom]: shouldShowBorderBottom,
        })}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>
          <MUICheckbox
            checked={isExpanded}
            color="default"
            icon={<Icon icon={icons.angleRight} size={ICON_SIZE.SM} />}
            checkedIcon={<Icon icon={icons.angleDown} size={ICON_SIZE.SM} />}
            disableRipple
          />
        </span>
        <span>
          <Checkbox
            isChecked={selectionState.isPackageChecked(snippetPackage.id)}
            isIndeterminate={selectionState.isPackageIndeterminate(snippetPackage.id)}
            onChange={() => selectionState.togglePackage(snippetPackage.id)}
            shouldStopPropagation
            isRippleDisabled
          />
        </span>
        <span>
          <span className={cx({ [styles.packageRejected]: snippetPackage.isFullyRejected })}>
            {snippetPackage.name}
          </span>
        </span>
        <span>{snippetPackage.versionCount} {plural(snippetPackage.versionCount, 'version')}</span>
        <span>
          <IssueCounts
            id={snippetPackage.id}
            isRejected={snippetPackage.isFullyRejected}
            issueCounts={snippetPackage.issueCounts}
          />
        </span>
        <span>{snippetPackage.matchCount}</span>
        <span>
          <PackageLabelsList
            labels={snippetPackage.labels}
            labelPrefix=""
            isLeftAligned
          />
        </span>
      </div>
      {isExpanded && (
        <>
          {isInitialLoading && (
            // By default, we load at most 10 snippets per page.
            _.times(Math.min(snippetPackage.versionCount, 10), (i) => <LoaderRow key={i} />)
          )}
          {!isInitialLoading && data?.pages.map((page) =>
            page.results.map((snippet) => (
              <SnippetRow
                key={`snippet-${snippet.id}`}
                snippet={snippet}
                selectionState={selectionState}
                shouldShowBorderTop={false}
                shouldShowBorderBottom={false}
                onSelectSnippet={(selectedSnippet) => setQuery({
                  ...queryParams,
                  selectedSnippetId: selectedSnippet.id,
                })}
              />
            )),
          )}
          {hasNextPage && (
            <div className={styles.loadMoreButtonContainer}>
              <LoaderButton onClick={() => fetchNextPage()} isLoading={isFetchingNextPage} type="secondary">
                {isFetchingNextPage ? 'Loading...' : 'Load More'}
              </LoaderButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SnippetPackageRow;
